import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UITableBaseColumn } from '@shared/ui';
import { Chart } from 'chart.js';
import { DashboardParkingLoadStatisticModel } from '../../models';

@Component({
  selector: 'aggregator-dashboard-parking-load-chart',
  templateUrl: './parking-load-chart.component.html',
  styleUrl: './parking-load-chart.component.scss',
  standalone: true,
  imports: [],
})
export class DashboardParkingLoadChartComponent extends UITableBaseColumn implements OnInit {
  @ViewChild('chartRef', { static: true })
  chartCanvas!: ElementRef<HTMLCanvasElement>;

  chart!: Chart;

  get dynamics(): { date: string; value: number }[] {
    return (this.data as DashboardParkingLoadStatisticModel)?.averageLoad?.dynamics ?? [];
  }

  get period(): number {
    return (this.data as DashboardParkingLoadStatisticModel)?.averageLoad?.period ?? 1;
  }

  ngOnInit(): void {
    this.updateChart();
  }

  getLabelByPeriod(period: number): string {
    switch (period) {
      case 1:
        return 'На этой неделе';
      case 2:
        return 'За этот месяц';
      case 3:
        return 'За этот год';
      default:
        return 'На этой неделе';
    }
  }

  private normalizeChartData(
    period: number,
    rawData: { date: string; value: number }[],
  ): { date: string; value: number }[] {
    const now = new Date();
    const generated: { date: string; value: number }[] = [];

    if (period === 1) {
      // Неделя — каждый час
      const currentDay = now.getDay(); // 0 (вс) - 6 (сб)
      const diffToMonday = currentDay === 0 ? -6 : 1 - currentDay;
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      start.setDate(now.getDate() + diffToMonday);

      for (let i = 0; i < 7 * 24; i++) {
        const d = new Date(start.getTime() + i * 60 * 60 * 1000);
        generated.push({
          date: d.toISOString().slice(0, 13) + ':00:00',
          value: 0,
        });
      }
    } else if (period === 2) {
      // Месяц — каждый день
      const year = now.getFullYear();
      const month = now.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      for (let day = 1; day <= daysInMonth; day++) {
        const d = new Date(year, month, day);
        generated.push({
          date: d.toISOString().slice(0, 10) + 'T00:00:00',
          value: 0,
        });
      }
    } else if (period === 3) {
      // Год — по неделям
      const year = now.getFullYear();
      const firstDay = new Date(year, 0, 1);
      for (let i = 0; i < 52; i++) {
        const d = new Date(firstDay.getTime() + i * 7 * 24 * 60 * 60 * 1000);
        generated.push({
          date: d.toISOString().slice(0, 10) + 'T00:00:00',
          value: 0,
        });
      }
    }

    const filled = generated.map(g => {
      let match;
      if (period === 1) {
        match = rawData.find(d => d.date.slice(0, 13) === g.date.slice(0, 13));
      } else {
        match = rawData.find(d => d.date.slice(0, 10) === g.date.slice(0, 10));
      }

      return {
        date: g.date,
        value: match ? match.value : 0,
      };
    });

    return filled;
  }

  updateChart(): void {
    if (this.period === 0) return;

    console.log(this.data, 'Полученные данные для графика');

    const fullData = this.normalizeChartData(this.period, this.dynamics);

    if (this.chart) {
      this.chart.destroy();
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) return;

    const gradient = ctx.createLinearGradient(0, 0, 0, 30);
    gradient.addColorStop(0, '#FF4949');
    gradient.addColorStop(0.4, '#FFE011');
    gradient.addColorStop(1, '#35D43C');

    console.log('FullData:', fullData);
    console.log('Label Count:', fullData.map(d => d.date).length);
    console.log('Data Count:', fullData.map(d => d.value).length);

    this.chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: fullData.map(d => {
          const date = new Date(d.date);
          if (this.period === 1) {
            return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
          } else if (this.period === 2) {
            return date.toLocaleDateString('ru-RU', { day: '2-digit' });
          } else {
            return date.toLocaleDateString('ru-RU', { month: 'short', day: '2-digit' });
          }
        }),
        datasets: [
          {
            label: this.getLabelByPeriod(this.period),
            data: fullData.map(d => d.value ?? 0),
            backgroundColor: gradient,
            tension: 0.4,
            fill: true,
            borderWidth: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
        ],
      },
      options: {
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: { beginAtZero: true, display: false },
          x: {
            display: false,
            grid: { display: false },
          },
        },
      },
    });
  }
}
